'use client';

import { useEffect, useState } from 'react';

import {
    Container,
    Row,
    Rows,
    Section,
    Spacer,
    SpacerSize,
    Title,
    TitleLevels,
    TitleMarkup,
    TrilogyColor,
    TypographyAlign,
} from '@bytel/trilogy-react';

import { FaiTabsEnum, TabConfig } from '@app-types/home-fai';
import { FaiType, OffersByCategoryCode } from '@app-types/plan';

import { Breadcrumb as BreadcrumbComponent } from '@components/breadcrumb';
import { FaiOffersByTabs } from '@components/fai-offers-by-tabs';
import { FaiUpsellModalContainer } from '@components/fai-upsell-modal';
import { Faq } from '@components/faq';
import { Legals } from '@components/legals';
import { SSIBlock } from '@components/ssi-block';
import { TriggerLogin } from '@components/trigger-login';

import { partials } from '@helpers/partials';

import { PlanTypeId } from '@constants/plan';

import { useActiveTab, useAddToCart, useEligibilityContext, useUpsellModal } from '@home-fai/hooks';

import {
    ProviderHomeSsiBlockConfig,
    ProviderHomeTabConfig,
    getProviderHomeTabConfig,
} from '../config/provider-home.config';
import { StickyBar } from './sticky-bar';

interface ProviderHomeProps {
    defaultPlans: FaiType[];
    defaultTab?: FaiTabsEnum;
    tabConfig: TabConfig;
    offersByCategory: OffersByCategoryCode;
}

export function ProviderHome({ defaultPlans, defaultTab, tabConfig, offersByCategory }: Readonly<ProviderHomeProps>) {
    const categoriesToShow: PlanTypeId[] = [PlanTypeId.FAI_HOME];
    const [activeTab, setActiveTab] = useActiveTab({ defaultTab, tabConfig });
    const { plans, idPanierCorbis, homeZone } = useEligibilityContext({ defaultPlans });
    const { upsellModal, showUpsellModal } = useUpsellModal({ defaultPlans });
    const { onAddToCart } = useAddToCart({ plans, idPanierCorbis, homeZone, showUpsellModal });
    const [tabUiConfig, setTabUiConfig] = useState<ProviderHomeTabConfig>(getProviderHomeTabConfig(activeTab));

    useEffect(() => {
        setTabUiConfig(getProviderHomeTabConfig(activeTab));
    }, [activeTab]);

    const renderSsiBlock = ({ url }: ProviderHomeSsiBlockConfig) => <SSIBlock key={url} url={url} />;

    const renderFaq = () => {
        const faqConfig = tabUiConfig.faq;

        return (
            <Row className={faqConfig.wrapper?.rowClassName}>
                <Spacer size={SpacerSize.EIGHT} />
                <Container medium>
                    <Faq title={faqConfig.title} titleAlt={faqConfig.titleAlt} url={faqConfig.url} />
                </Container>
            </Row>
        );
    };

    return (
        <>
            <Section backgroundColor={TrilogyColor.NEUTRAL_FADE}>
                <Container size='md'>
                    <Title markup={TitleMarkup.H1} level={TitleLevels.ONE} typo={TypographyAlign.TEXT_CENTERED}>
                        Nos box internet
                    </Title>
                    <SSIBlock url={partials.MARKETING.FAI_HOME_SLIDER} />
                    <FaiOffersByTabs
                        setActiveTab={setActiveTab}
                        defaultTab={activeTab}
                        tabConfig={tabConfig}
                        offersByCategory={offersByCategory}
                        categoriesToShow={categoriesToShow}
                        plans={plans}
                        onAddToCart={onAddToCart}
                        showComparatorSelection
                    />
                </Container>
            </Section>
            <div>{tabUiConfig.ssiBlocks.map((config) => renderSsiBlock(config))}</div>

            <Section backgroundColor={TrilogyColor.NEUTRAL_FADE}>
                <Container size='md'>
                    <Rows>
                        {renderFaq()}
                        <Row>
                            <BreadcrumbComponent
                                elements={[
                                    {
                                        name: 'home',
                                        label: 'Accueil',
                                        href: '/',
                                    },
                                    {
                                        name: 'home FAI',
                                        label: 'Offres internet',
                                    },
                                ]}
                            />
                        </Row>
                        <Row>
                            {tabUiConfig.legals && (
                                <Legals
                                    key={tabUiConfig.legals.id}
                                    id={tabUiConfig.legals.id}
                                    url={tabUiConfig.legals.url}
                                />
                            )}
                        </Row>
                    </Rows>
                </Container>
            </Section>
            <StickyBar activeTab={activeTab} />
            <TriggerLogin />
            {upsellModal && <FaiUpsellModalContainer {...upsellModal} />}
        </>
    );
}
